import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { GlassPanel, GlassButton, GlassInput, GlassSelect, GlassCard } from '../components/GlassUI';
import { FaWhatsapp, FaCheck, FaUser, FaPhone, FaShareAlt } from 'react-icons/fa';
import QrCode from '../components/QrCode';
import { downloadVCard, shareVCard } from '../utils/download';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Fallback shop details shown until the owner fills them in (Profile →
// Business / Shop Details). Live values are fetched from the public endpoint.
const DEFAULT_SHOP = {
  companyName: 'Apni Jewellers',
  phone: '',
  whatsapp: '',
  website: '',
  address: '',
};

const EMPTY_FORM = {
  customerName: '',
  cellNumber: '',
  whatsappNumber: '',
  location: '',
  profession: 'OTHER',
  jewelleryTypeInquired: '',
  notes: '',
};

export const CustomerSelfServicePage: React.FC = () => {
  // The counter QR encodes this page with a query string:
  //   ?ref=<customerId>  → return visit, prefill the customer's saved details
  //   ?source=QR_SCAN    → first visit via the printed QR
  //   ?invoice=...       → arrived from an invoice status QR
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';
  const source = searchParams.get('source') || '';
  const invoice = searchParams.get('invoice') || '';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(!!ref);
  const [prefilled, setPrefilled] = useState(false);
  const [submitted, setSubmitted] = useState<any>(null);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // The shop's own details for the header + "Save Our Contact" QR. Public,
  // unauthenticated — fails softly back to DEFAULT_SHOP.
  const [shop, setShop] = useState({ ...DEFAULT_SHOP });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/settings/public`);
        const s = res.data?.store;
        if (!cancelled && s) setShop((prev) => ({ ...prev, ...s }));
      } catch {
        // Keep the defaults if the shop has not configured details yet.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull the customer's own saved details so a returning visitor only has to
  // review and save. Fails softly: a bad/stale ref just means a blank form.
  useEffect(() => {
    if (!ref) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/customers/public-info`, {
          params: { ref },
        });
        const c = res.data?.customer;
        if (!cancelled && c) {
          setFormData((prev) => ({
            ...prev,
            customerName: c.customerName || '',
            cellNumber: c.cellNumber || '',
            whatsappNumber: c.whatsappNumber || '',
            location: c.location || '',
            profession: c.profession || 'OTHER',
          }));
          setPrefilled(true);
        }
      } catch {
        // Unknown reference — continue with an empty form.
      } finally {
        if (!cancelled) setPrefilling(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.customerName || !formData.cellNumber) {
      alert('Please fill in your name and phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/customers/public-form`, {
        ...formData,
        ref: ref || undefined,
        source: source || (ref ? 'QR_RETURN' : undefined),
        invoice: invoice || undefined,
      });
      setSubmitted(res.data.customer);
      setStep(3);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const shopName = shop.companyName || DEFAULT_SHOP.companyName;
  const shopPhone = shop.phone || '';
  const shopWhatsapp = shop.whatsapp || shop.phone || '';

  const whatsappLink = (phone: string) =>
    `https://wa.me/${phone.replace(/[^0-9]/g, '')}`;

  const vCardData = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${shopName}`,
    `ORG:${shopName}`,
    shopPhone ? `TEL;TYPE=WORK,VOICE:${shopPhone}` : '',
    shopWhatsapp ? `TEL;TYPE=WHATSAPP:${shopWhatsapp}` : '',
    shop.website ? `URL:${shop.website}` : '',
    shop.address ? `ADR:;;${shop.address};;;;` : '',
    'END:VCARD',
  ].filter(Boolean).join('\n');

  if (prefilling) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 flex items-center justify-center p-4'>
        <div className='text-center'>
          <div className='animate-spin w-10 h-10 border-4 border-white/40 border-t-white rounded-full mx-auto mb-4' />
          <p className='text-white'>Loading your details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 flex items-center justify-center p-4'>
      <div className='w-full max-w-lg'>
        <div className='text-center mb-6'>
          <h1 className='text-3xl font-bold text-white'>{shopName}</h1>
          <p className='text-blue-100'>Jewelry Store - Customer Form</p>
        </div>

        {/* Step 1: Personal Info */}
        {step === 1 && (
          <GlassPanel className='p-8'>
            {prefilled && (
              <div className='mb-4 flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800'>
                <FaCheck className='mt-0.5 shrink-0' />
                <span>Welcome back! We&apos;ve filled in your saved details — just check them and save.</span>
              </div>
            )}
            <h2 className='text-2xl font-bold text-slate-900 mb-6'>Your Details</h2>
            <div className='space-y-4'>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Full Name *</label>
                <GlassInput
                  name='customerName'
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder='Your name'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Phone Number *</label>
                <GlassInput
                  name='cellNumber'
                  value={formData.cellNumber}
                  onChange={handleChange}
                  placeholder='+91...'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>WhatsApp Number</label>
                <GlassInput
                  name='whatsappNumber'
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  placeholder='WhatsApp number'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Location</label>
                <GlassInput
                  name='location'
                  value={formData.location}
                  onChange={handleChange}
                  placeholder='Your city/location'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Profession</label>
                <GlassSelect name='profession' value={formData.profession} onChange={handleChange}>
                  <option value='DOCTOR'>Doctor</option>
                  <option value='LAWYER'>Lawyer</option>
                  <option value='TEACHER'>Teacher</option>
                  <option value='SELF_BUSINESS'>Self Business</option>
                  <option value='OTHER'>Other</option>
                </GlassSelect>
              </div>
              <GlassButton variant='primary' className='w-full mt-4' onClick={() => setStep(2)}>
                Next - What are you looking for?
              </GlassButton>
            </div>
          </GlassPanel>
        )}

        {/* Step 2: Interest */}
        {step === 2 && (
          <GlassPanel className='p-8'>
            <h2 className='text-2xl font-bold text-slate-900 mb-6'>Your Interest</h2>
            <div className='space-y-4'>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Jewellery Type Interested In</label>
                <GlassInput
                  name='jewelleryTypeInquired'
                  value={formData.jewelleryTypeInquired}
                  onChange={handleChange}
                  placeholder='e.g., Necklace, Ring, Bracelet, Earrings'
                />
              </div>
              <div>
                <label className='block text-slate-700 font-semibold mb-2'>Notes / Requirements</label>
                <textarea
                  name='notes'
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder='Any specific requirements...'
                  className='w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all'
                  rows={4}
                />
              </div>
              <div className='flex gap-3 pt-4'>
                <GlassButton variant='secondary' onClick={() => setStep(1)} className='flex-1'>
                  Back
                </GlassButton>
                <GlassButton variant='primary' className='flex-1' onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit'}
                </GlassButton>
              </div>
            </div>
          </GlassPanel>
        )}

        {/* Step 3: Success + WhatsApp + QR */}
        {step === 3 && submitted && (
          <GlassPanel className='p-8 text-center'>
            <div className='text-5xl mb-4'><FaCheck className='text-green-500 mx-auto' /></div>
            <h2 className='text-2xl font-bold text-slate-900 mb-2'>Thank You!</h2>
            <p className='text-slate-600 mb-6'>
              {submitted.updatedExisting
                ? `Thanks! We've updated your details with ${shopName}.`
                : `Your details have been submitted to ${shopName}.`}
              {submitted.whatsapp && ' We will contact you on WhatsApp.'}
            </p>

            <div className='grid gap-4 mb-6'>
              {(shopPhone || shopWhatsapp) && (
                <GlassCard className='bg-green-50 border-green-200'>
                  <h3 className='font-bold text-slate-900 mb-3'>Save Our Contact</h3>
                  <p className='text-sm text-slate-600 mb-4'>
                    Tap the button to save {shopName}&apos;s number to this phone, or share it / scan the QR.
                  </p>
                  <div className='flex flex-col sm:flex-row gap-2 mb-4'>
                    <GlassButton
                      variant='primary'
                      className='flex-1'
                      onClick={() => downloadVCard(`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact`, vCardData)}
                    >
                      <FaPhone className='mr-2' /> Save Contact
                    </GlassButton>
                    <GlassButton
                      variant='secondary'
                      className='flex-1'
                      onClick={() => shareVCard(`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact`, vCardData, `${shopName} contact`, shop.website || undefined)}
                    >
                      <FaShareAlt className='mr-2' /> Share
                    </GlassButton>
                  </div>
                  <div className='flex justify-center mb-4'>
                    <QrCode
                      value={vCardData}
                      size={192}
                      fileName={`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact-qr`}
                      showDownload
                      showShare
                      shareTitle={`Save ${shopName} as a contact`}
                    />
                  </div>
                  <div className='space-y-1 text-sm text-slate-700'>
                    {shopPhone && <p><strong>Phone:</strong> {shopPhone}</p>}
                    {shop.whatsapp && <p><strong>WhatsApp:</strong> {shop.whatsapp}</p>}
                    {shop.website && <p><strong>Website:</strong> {shop.website}</p>}
                  </div>
                  {shopWhatsapp && (
                    <a
                      href={whatsappLink(shopWhatsapp)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 mt-4 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all'
                    >
                      <FaWhatsapp size={20} /> Chat on WhatsApp
                    </a>
                  )}
                </GlassCard>
              )}

              <GlassCard className='bg-blue-50 border-blue-200 text-left'>
                <h3 className='font-bold text-slate-900 mb-2 flex items-center gap-2'>
                  <FaUser className='text-blue-500' /> Your Submitted Info
                </h3>
                <div className='space-y-1 text-sm text-slate-600'>
                  <p><strong>Name:</strong> {submitted.name}</p>
                  <p><strong>Phone:</strong> {submitted.phone}</p>
                  {submitted.whatsapp && <p><strong>WhatsApp:</strong> {submitted.whatsapp}</p>}
                  {formData.jewelleryTypeInquired && <p><strong>Interested in:</strong> {formData.jewelleryTypeInquired}</p>}
                </div>
              </GlassCard>
            </div>

            <GlassButton variant='primary' className='w-full' onClick={() => { setStep(1); setSubmitted(null); setFormData({ ...EMPTY_FORM }); }}>
              Submit Another Entry
            </GlassButton>
          </GlassPanel>
        )}
      </div>
    </div>
  );
};

export default CustomerSelfServicePage;
